import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    registerUser,
    sendRegistrationOtp,
    verifyRegistrationOtp
} from '../../services/userService';
import { toast } from 'react-toastify';
import PasswordField from '../../components/PasswordField';

const RED = '#B22222';
const DARK_RED = '#8B0000';
const GOLD = '#FFD700';

const RESEND_SECONDS = 30;

const UserRegister = () => {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [cooldown, setCooldown] = useState(0);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        otp: '',
        phoneNumber: '',
        address: '',
        password: '',
        confirmPassword: ''
    });

    const navigate = useNavigate();

    useEffect(() => {
        if (cooldown <= 0) return;
        const t = setTimeout(
            () => setCooldown(cooldown - 1), 1000);
        return () => clearTimeout(t);
    }, [cooldown]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    // Email hamesha saaf karke use karo
    const cleanEmail = () =>
        formData.email.trim().toLowerCase();

    // ===== Step 1 : OTP bhejo =====
    const handleSendOtp = async () => {
        const email = cleanEmail();

        if (!formData.firstName.trim()) {
            return toast.warning('Please enter your first name');
        }
        if (!formData.lastName.trim()) {
            return toast.warning('Please enter your last name');
        }
        if (!/^\S+@\S+\.\S+$/.test(email)) {
            return toast.warning('Enter a valid email');
        }

        setLoading(true);
        try {
            await sendRegistrationOtp(
                email, formData.firstName.trim());
            toast.success('OTP sent to your email');
            setCooldown(RESEND_SECONDS);
            setStep(2);
        } catch (error) {
            toast.error(error.response?.data?.message
                || 'Could not send OTP');
        } finally {
            setLoading(false);
        }
    };

    // ===== Step 2 : OTP verify karo =====
    const handleVerifyOtp = async () => {
        if (formData.otp.length !== 6) {
            return toast.warning('Enter the 6 digit OTP');
        }

        setLoading(true);
        try {
            await verifyRegistrationOtp(
                cleanEmail(), formData.otp.trim());
            toast.success('Email verified');
            setStep(3);
        } catch (error) {
            toast.error(error.response?.data?.message
                || 'Invalid OTP');
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (cooldown > 0) return;
        setLoading(true);
        try {
            await sendRegistrationOtp(
                cleanEmail(), formData.firstName.trim());
            toast.success('New OTP sent');
            setCooldown(RESEND_SECONDS);
            setFormData({ ...formData, otp: '' });
        } catch (error) {
            toast.error(error.response?.data?.message
                || 'Could not resend OTP');
        } finally {
            setLoading(false);
        }
    };

    // ===== Step 3 : Register =====
    const handleRegister = async () => {
        if (!/^[6-9]\d{9}$/.test(formData.phoneNumber)) {
            return toast.warning(
                'Enter a valid 10 digit mobile number');
        }
        if (formData.password.length < 8) {
            return toast.warning(
                'Password must be at least 8 characters');
        }
        if (formData.password !== formData.confirmPassword) {
            return toast.warning('Passwords do not match');
        }

        setLoading(true);
        try {
            await registerUser({
                firstName: formData.firstName.trim(),
                lastName: formData.lastName.trim(),
                email: cleanEmail(),
                password: formData.password,
                phoneNumber: formData.phoneNumber.trim(),
                address: formData.address.trim()
            });
            toast.success('Registration successful! '
                + 'Please login.');
            navigate('/login');
        } catch (error) {
            toast.error(error.response?.data?.message
                || 'Registration failed');
        } finally {
            setLoading(false);
        }
    };

    const goBack = () => {
        if (step === 1) return navigate('/');
        // OTP verify hone ke baad wapas nahi ja sakte
        if (step === 3) return;
        setStep(step - 1);
    };

    const steps = [
        { n: 1, label: 'Email' },
        { n: 2, label: 'Verify' },
        { n: 3, label: 'Details' }
    ];

    const primaryAction =
        step === 1 ? handleSendOtp
        : step === 2 ? handleVerifyOtp
        : handleRegister;

    const primaryLabel =
        loading ? 'Please wait...'
        : step === 1 ? 'Send OTP'
        : step === 2 ? 'Verify'
        : 'Create account';

    return (
        <div style={{ backgroundColor: '#FFF5F5' }}
            className="pb-screen pb-has-action-bar">

            {/* ===== HEADER ===== */}
            <div style={{
                backgroundColor: DARK_RED,
                padding: '14px 16px'
            }}>
                <div className="d-flex align-items-center gap-2">
                    {step !== 3 && (
                        <button
                            onClick={goBack}
                            style={{
                                background: 'none',
                                border: 'none',
                                color: GOLD,
                                fontSize: '22px',
                                lineHeight: 1,
                                padding: 0
                            }}>
                            ←
                        </button>
                    )}
                    <div>
                        <p className="mb-0 fw-bold"
                            style={{
                                color: GOLD,
                                fontSize: '15px'
                            }}>
                            Create your account
                        </p>
                        <p className="mb-0"
                            style={{
                                color: '#FFE4B5',
                                fontSize: '12px'
                            }}>
                            Book poojaris in a few taps
                        </p>
                    </div>
                </div>
            </div>

            {/* ===== STEPS ===== */}
            <div className="pb-steps">
                {steps.map(s => (
                    <div key={s.n}
                        className={`pb-step ${step === s.n
                            ? 'active'
                            : step > s.n ? 'done' : ''}`}>
                        <div className="pb-step-bar"></div>
                        <p className="pb-step-label">
                            {s.n} · {s.label}
                        </p>
                    </div>
                ))}
            </div>

            <div className="container"
                style={{ maxWidth: '520px' }}>
                <div className="py-4">

                    {/* ============ STEP 1 ============ */}
                    {step === 1 && (
                        <>
                            <p className="text-muted mb-4"
                                style={{ fontSize: '14px' }}>
                                We will send a 6 digit code to
                                your email to make sure it is
                                really you.
                            </p>

                            <div className="mb-3">
                                <label className="form-label fw-bold"
                                    style={{
                                        color: DARK_RED,
                                        fontSize: '13px'
                                    }}>
                                    First name
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="firstName"
                                    placeholder="Your first name"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    style={{ borderColor: GOLD }}
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label fw-bold"
                                    style={{
                                        color: DARK_RED,
                                        fontSize: '13px'
                                    }}>
                                    Last name
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="lastName"
                                    placeholder="Your last name"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    style={{ borderColor: GOLD }}
                                />
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-bold"
                                    style={{
                                        color: DARK_RED,
                                        fontSize: '13px'
                                    }}>
                                    Email address
                                </label>
                                <input
                                    type="email"
                                    className="form-control"
                                    name="email"
                                    placeholder="you@example.com"
                                    value={formData.email}
                                    onChange={handleChange}
                                    autoComplete="email"
                                    autoCapitalize="none"
                                    spellCheck="false"
                                    style={{ borderColor: GOLD }}
                                />
                            </div>
                        </>
                    )}

                    {/* ============ STEP 2 ============ */}
                    {step === 2 && (
                        <>
                            <p className="text-muted mb-4"
                                style={{ fontSize: '14px' }}>
                                We sent a 6 digit code to{' '}
                                <strong style={{
                                    color: DARK_RED
                                }}>
                                    {cleanEmail()}
                                </strong>
                            </p>

                            <div className="mb-3">
                                <label className="form-label fw-bold"
                                    style={{
                                        color: DARK_RED,
                                        fontSize: '13px'
                                    }}>
                                    Enter OTP
                                </label>
                                <input
                                    type="tel"
                                    className="form-control text-center"
                                    name="otp"
                                    placeholder="000000"
                                    value={formData.otp}
                                    maxLength="6"
                                    autoFocus
                                    inputMode="numeric"
                                    onChange={e => {
                                        const v = e.target.value
                                            .replace(/\D/g, '');
                                        setFormData({
                                            ...formData,
                                            otp: v
                                        });
                                    }}
                                    style={{
                                        borderColor: GOLD,
                                        fontSize: '26px',
                                        letterSpacing: '10px',
                                        fontWeight: 700,
                                        color: DARK_RED
                                    }}
                                />
                            </div>

                            <div className="text-center">
                                {cooldown > 0 ? (
                                    <small className="text-muted">
                                        Resend code in {cooldown}s
                                    </small>
                                ) : (
                                    <button
                                        className="btn btn-sm p-0"
                                        style={{
                                            color: RED,
                                            border: 'none',
                                            background: 'none',
                                            fontSize: '14px',
                                            minHeight: 'auto',
                                            fontWeight: 600
                                        }}
                                        onClick={handleResend}
                                        disabled={loading}>
                                        Resend code
                                    </button>
                                )}
                            </div>

                            <div className="p-3 mt-4"
                                style={{
                                    backgroundColor: '#FFF8F0',
                                    borderRadius: '10px'
                                }}>
                                <small className="text-muted">
                                    Code not in your inbox? Check
                                    the spam folder. It expires in
                                    10 minutes.
                                </small>
                            </div>
                        </>
                    )}

                    {/* ============ STEP 3 ============ */}
                    {step === 3 && (
                        <>
                            <div className="p-3 mb-4"
                                style={{
                                    backgroundColor: '#EAF3DE',
                                    borderRadius: '10px'
                                }}>
                                <small style={{
                                    color: '#27500A'
                                }}>
                                    ✓ {cleanEmail()} verified
                                </small>
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-bold"
                                    style={{
                                        color: DARK_RED,
                                        fontSize: '13px'
                                    }}>
                                    Mobile number
                                </label>
                                <input
                                    type="tel"
                                    className="form-control"
                                    name="phoneNumber"
                                    placeholder="10 digit number"
                                    value={formData.phoneNumber}
                                    maxLength="10"
                                    inputMode="numeric"
                                    onChange={e => {
                                        const v = e.target.value
                                            .replace(/\D/g, '');
                                        setFormData({
                                            ...formData,
                                            phoneNumber: v
                                        });
                                    }}
                                    style={{ borderColor: GOLD }}
                                />
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-bold"
                                    style={{
                                        color: DARK_RED,
                                        fontSize: '13px'
                                    }}>
                                    Address
                                </label>
                                <textarea
                                    className="form-control"
                                    name="address"
                                    rows="2"
                                    placeholder="Where do you usually hold poojas?"
                                    value={formData.address}
                                    onChange={handleChange}
                                    style={{ borderColor: GOLD }}>
                                </textarea>
                                <small className="text-muted">
                                    This will be pre-filled when
                                    you book
                                </small>
                            </div>

                            <div className="mb-3">
                                <PasswordField
                                    label="Password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    placeholder="Min 8 characters"
                                />
                            </div>

                            <div className="mb-3">
                                <PasswordField
                                    label="Confirm password"
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    placeholder="Type it again"
                                />
                            </div>
                        </>
                    )}

                    <div className="text-center mt-4">
                        <small className="text-muted">
                            Already have an account?{' '}
                            <Link to="/login"
                                style={{
                                    color: RED,
                                    fontWeight: 600
                                }}>
                                Login
                            </Link>
                        </small>
                    </div>
                </div>
            </div>

            {/* ===== STICKY ACTION BAR ===== */}
            <div className="pb-action-bar">
                <small className="text-muted">
                    Step {step} of 3
                </small>
                <button
                    className="btn fw-bold px-4"
                    style={{
                        backgroundColor: RED,
                        color: GOLD,
                        border: 'none',
                        borderRadius: '10px'
                    }}
                    disabled={loading}
                    onClick={primaryAction}>
                    {primaryLabel}
                </button>
            </div>
        </div>
    );
};

export default UserRegister;